import { CLAIM_TYPES, type ClaimType } from '@ambernord/shared';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Select,
  SkeletonRows,
  StatusBadge,
  THead,
  TableShell,
  Td,
  Th,
  Tr,
  cn,
  useToast,
} from '@ambernord/ui';
import { ShieldCheck } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDate, formatNumber, productName } from '../lib/format.js';

interface ClaimItem {
  id: string;
  type: string;
  status: string;
  reportedAt: string | null;
  deadline: string | null;
  deviationPrice: string | null;
}

interface SupplyOrderOption {
  id: string;
  ref: string;
  status: string;
  terms?: { productId?: string } | null;
}

interface ProductItem {
  id: string;
  namesI18n: Record<string, string>;
}

export default function Claims() {
  const { t, i18n } = useTranslation(['claims', 'statuses']);
  const lang = i18n.language;
  const { toast } = useToast();

  const [items, setItems] = useState<ClaimItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [supplyOrders, setSupplyOrders] = useState<SupplyOrderOption[]>([]);
  const [productMap, setProductMap] = useState<Record<string, Record<string, string>>>({});

  const [type, setType] = useState<ClaimType>(CLAIM_TYPES[0]);
  const [supplyOrderId, setSupplyOrderId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await api.get<{ items: ClaimItem[] }>('/api/claims');
    if (!res.ok) {
      setError(t('errors.load', { status: res.status }));
      setItems([]);
      return;
    }
    setError(null);
    setItems(res.data.items ?? []);
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Piegādes pasūtījumi izvēlnei + produktu nosaukumi — labākā piepūle
  // (klientam SO saraksts nav pieejams; tad izvēlne paliek tikai ar "—").
  useEffect(() => {
    let active = true;
    void (async () => {
      const [soRes, prodRes] = await Promise.all([
        api.get<{ items: SupplyOrderOption[] }>('/api/supply-orders'),
        api.get<{ items: ProductItem[] }>('/api/products'),
      ]);
      if (!active) return;
      if (soRes.ok) setSupplyOrders(soRes.data.items ?? []);
      if (prodRes.ok) {
        const map: Record<string, Record<string, string>> = {};
        for (const p of prodRes.data.items ?? []) map[p.id] = p.namesI18n;
        setProductMap(map);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  function soLabel(so: SupplyOrderOption): string {
    const pid = so.terms?.productId;
    const prod = pid && productMap[pid] ? productName(productMap[pid], lang) : '—';
    return `${so.ref} — ${prod} — ${t(`statuses:${so.status}`, { defaultValue: so.status })}`;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const res = await api.post<{ error?: string }>('/api/claims', {
        type,
        ...(supplyOrderId ? { supplyOrderId } : {}),
      });
      if (!res.ok) {
        setFormError(res.data.error ?? t('errors.create', { status: res.status }));
        return;
      }
      setSupplyOrderId('');
      toast(t('toast.created'));
      await load();
    } catch {
      setFormError(t('errors.createGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  function deadlineCell(c: ClaimItem) {
    if (!c.deadline) return <span className="text-slate-500 dark:text-slate-400">—</span>;
    const overdue = c.status === 'open' && new Date(c.deadline).getTime() < Date.now();
    return (
      <span
        className={cn(
          overdue
            ? 'font-semibold text-red-600 dark:text-red-400'
            : 'text-slate-500 dark:text-slate-400',
        )}
      >
        {formatDate(c.deadline, lang)}
      </span>
    );
  }

  const loaded = items !== null;

  return (
    <AppLayout title={t('title')}>
      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader title={t('report.heading')} subtitle={t('report.explainer')} />
          <CardBody>
            <form
              className="flex flex-col gap-4 sm:flex-row sm:items-end"
              onSubmit={(e) => void onSubmit(e)}
              noValidate
            >
              <Field label={t('form.type')} htmlFor="claim-type" className="sm:w-48">
                <Select
                  id="claim-type"
                  value={type}
                  onChange={(e) => setType(e.target.value as ClaimType)}
                >
                  {CLAIM_TYPES.map((c) => (
                    <option key={c} value={c}>
                      {t(`type.${c}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('form.supplyOrder')} htmlFor="claim-order" className="flex-1">
                <Select
                  id="claim-order"
                  value={supplyOrderId}
                  onChange={(e) => setSupplyOrderId(e.target.value)}
                >
                  <option value="">{t('form.none')}</option>
                  {supplyOrders.map((so) => (
                    <option key={so.id} value={so.id}>
                      {soLabel(so)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button type="submit" disabled={submitting}>
                {submitting ? t('actions.saving') : t('actions.create')}
              </Button>
            </form>
            {formError && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
                {formError}
              </p>
            )}
          </CardBody>
        </Card>

        {!loaded && <SkeletonRows rows={4} />}
        {loaded && error && (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}

        {loaded && !error && items.length === 0 && (
          <EmptyState
            icon={<ShieldCheck className="h-6 w-6" aria-hidden />}
            title={t('empty.title')}
            hint={t('empty.hint')}
          />
        )}

        {loaded && !error && items.length > 0 && (
          <TableShell>
            <THead>
              <tr>
                <Th>{t('table.type')}</Th>
                <Th>{t('table.status')}</Th>
                <Th>{t('table.reported')}</Th>
                <Th>{t('table.deadline')}</Th>
                <Th>{t('table.deviationPrice')}</Th>
              </tr>
            </THead>
            <tbody>
              {items.map((c) => (
                <Tr key={c.id}>
                  <Td>
                    <StatusBadge
                      status={c.type}
                      label={t(`type.${c.type}`, { defaultValue: c.type })}
                    />
                  </Td>
                  <Td>
                    <StatusBadge
                      status={c.status}
                      label={t(`statuses:${c.status}`, { defaultValue: c.status })}
                    />
                  </Td>
                  <Td className="text-slate-500 dark:text-slate-400">
                    {formatDate(c.reportedAt, lang)}
                  </Td>
                  <Td>{deadlineCell(c)}</Td>
                  <Td className="text-slate-500 dark:text-slate-400">
                    {formatNumber(c.deviationPrice, lang)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </div>
    </AppLayout>
  );
}
